import React from 'react';
import {
  Typography, Box, Button, Container, Card, CardContent,
  Avatar, Stack, useTheme, alpha, useMediaQuery
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

export default function NotFoundPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: isMobile ? 3 : isTablet ? 6 : 8,
        px: isMobile ? 1 : 2,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Card 
        sx={{ 
          boxShadow: isMobile ? 1 : 3, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: isMobile ? 2 : 3,
          textAlign: 'center',
          overflow: 'visible',
          mx: 'auto',
          width: '100%',
          maxWidth: isMobile ? '95vw' : '100%'
        }}
      >
        <CardContent 
          sx={{ 
            p: isMobile ? 2 : isTablet ? 4 : 6,
            position: 'relative'
          }}
        >
          {/* Error Icon Avatar - Mobile Responsive */}
          <Avatar
            sx={{
              bgcolor: theme.palette.error.main,
              width: isMobile ? 60 : isTablet ? 70 : 80,
              height: isMobile ? 60 : isTablet ? 70 : 80,
              mx: 'auto',
              mb: isMobile ? 2 : 3,
              boxShadow: `0 0 0 ${isMobile ? 6 : 8}px ${alpha(theme.palette.error.main, 0.1)}`,
              position: 'relative',
              top: isMobile ? -40 : isTablet ? -55 : -60
            }}
          >
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: isMobile ? 30 : isTablet ? 35 : 40 
              }} 
            />
          </Avatar>

          <Box 
            sx={{ 
              mt: isMobile ? -4 : isTablet ? -6 : -6,
              mb: isMobile ? 2 : isTablet ? 3 : 4
            }}
          >
            {/* 404 Number - Mobile Responsive */}
            <Typography
              variant="h1"
              fontWeight="bold"
              color="error"
              sx={{
                fontSize: isMobile ? '4rem' : isTablet ? '6rem' : '8rem',
                background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                lineHeight: 1,
                mb: isMobile ? 1 : 2,
                wordBreak: 'break-word',
                letterSpacing: isMobile ? '-2px' : '-4px'
              }}
            >
              404
            </Typography>

            {/* Title - Mobile Responsive */}
            <Typography 
              variant={isMobile ? "h5" : isTablet ? "h4" : "h4"}
              fontWeight="600" 
              gutterBottom
              sx={{ 
                mb: isMobile ? 1.5 : 2,
                fontSize: isMobile ? '1.25rem' : isTablet ? '1.75rem' : '2.125rem'
              }}
            >
              Page Not Found
            </Typography>

            {/* Description - Mobile Responsive */}
            <Typography 
              variant={isMobile ? "body2" : "body1"}
              color="text.secondary" 
              sx={{ 
                maxWidth: 400, 
                mx: 'auto',
                lineHeight: 1.6,
                mb: isMobile ? 2.5 : isTablet ? 3 : 4,
                fontSize: isMobile ? '0.875rem' : '1rem',
                px: isMobile ? 1 : 0
              }}
            >
              Sorry, we couldn't find the page you're looking for. The page might have been moved, 
              deleted, or you entered an incorrect URL.
            </Typography>

            {/* Action Buttons - Mobile Responsive */}
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 1.5 : 2} 
              justifyContent="center"
              sx={{ 
                mt: isMobile ? 2 : isTablet ? 3 : 4,
                width: '100%'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                size={isMobile ? "medium" : "large"}
                fullWidth={isMobile}
                sx={{
                  px: isMobile ? 2 : isTablet ? 3 : 4,
                  py: isMobile ? 1 : 1.5,
                  borderRadius: isMobile ? 1.5 : 2,
                  borderWidth: 2,
                  minWidth: isMobile ? 'auto' : 140,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                size={isMobile ? "medium" : "large"}
                fullWidth={isMobile}
                sx={{
                  px: isMobile ? 2 : isTablet ? 3 : 4,
                  py: isMobile ? 1 : 1.5,
                  borderRadius: isMobile ? 1.5 : 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  minWidth: isMobile ? 'auto' : 140,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: 4
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Go Home
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Additional Help Section - Mobile Responsive */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mt: isMobile ? 2 : isTablet ? 3 : 4 
        }}
      >
        <Typography 
          variant={isMobile ? "caption" : "body2"}
          color="text.secondary"
          sx={{ 
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            opacity: 0.8
          }}
        >
          If you believe this is an error, please contact support.
        </Typography>
      </Box>
    </Container>
  );
}